import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Home from "@/pages/Home";
import ExplorePage from "@/pages/ExplorePage";
import CityPage from "@/pages/CityPage";
import CategoryPage from "@/pages/CategoryPage";
import BusinessProfile from "@/pages/BusinessProfile";
import SearchPage from "@/pages/SearchPage";
import ListBusiness from "@/pages/ListBusiness";
import ListService from "@/pages/ListService";
import ServicesHub from "@/pages/ServicesHub";
import GetWebsite from "@/pages/GetWebsite";
import ContactPage from "@/pages/ContactPage";
import StaticPage from "@/pages/StaticPage";
import NearMe from "@/pages/NearMe";
import FavouritesPage from "@/pages/FavouritesPage";
import AuthCallback from "@/pages/AuthCallback";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import OwnerAuth from "@/pages/owner/OwnerAuth";
import OwnerDashboard from "@/pages/owner/OwnerDashboard";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function ProtectedAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  return children;
}

function ProtectedOwner({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user || (user.role !== "owner" && user.role !== "admin")) return <Navigate to="/owner" replace />;
  return children;
}

function Spinner() {
  return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
}

const withLayout = (el) => <Layout>{el}</Layout>;

function App() {
  return (
    <AuthProvider>
      <CustomerAuthProvider>
        <BrowserRouter>
          <ScrollTop />
          <Routes>
            <Route path="/" element={withLayout(<Home />)} />
            <Route path="/explore" element={withLayout(<ExplorePage />)} />
            <Route path="/near-me" element={withLayout(<NearMe />)} />
            <Route path="/favourites" element={withLayout(<FavouritesPage />)} />
            <Route path="/search" element={withLayout(<SearchPage />)} />
            <Route path="/list-business" element={withLayout(<ListBusiness />)} />
            <Route path="/list-service" element={withLayout(<ListService />)} />
            <Route path="/services" element={withLayout(<ServicesHub />)} />
            <Route path="/services/:city" element={withLayout(<ServicesHub />)} />
            <Route path="/services/:city/:category" element={withLayout(<CategoryPage kind="service" />)} />
            <Route path="/services/:city/:category/:slug" element={withLayout(<BusinessProfile kind="service" />)} />
            <Route path="/get-your-website" element={withLayout(<GetWebsite />)} />
            <Route path="/contact" element={withLayout(<ContactPage />)} />
            <Route path="/privacy" element={withLayout(<StaticPage page="privacy" />)} />
            <Route path="/terms" element={withLayout(<StaticPage page="terms" />)} />
            <Route path="/disclaimer" element={withLayout(<StaticPage page="disclaimer" />)} />

            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/payment/success" element={withLayout(<PaymentSuccess />)} />
            <Route path="/payment/cancel" element={withLayout(<PaymentCancel />)} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedAdmin><AdminDashboard /></ProtectedAdmin>} />

            <Route path="/owner" element={<OwnerAuth />} />
            <Route path="/owner/login" element={<OwnerAuth />} />
            <Route path="/owner/dashboard" element={<ProtectedOwner><OwnerDashboard /></ProtectedOwner>} />

            <Route path="/:city" element={withLayout(<CityPage />)} />
            <Route path="/:city/:category" element={withLayout(<CategoryPage />)} />
            <Route path="/:city/:category/:slug" element={withLayout(<BusinessProfile />)} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </CustomerAuthProvider>
    </AuthProvider>
  );
}

export default App;
